/**
 * SuperAdminDashboard Component - Platform-wide statistics
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Building2,
    Users,
    MessageSquare,
    Activity,
    TrendingUp,
    RefreshCw,
    AlertCircle,
    Loader2,
} from 'lucide-react';
import { Button } from '../ui/button';
import { cn } from '../../lib/utils';
import superAdminService from '../../services/superAdminService';
import { logger } from '../../utils/logger';

function StatCard({ title, value, icon: Icon, color, subtext }) {
    return (
        <div className="bg-white rounded-xl border p-6 shadow-sm">
            <div className="flex items-center justify-between">
                <div>
                    <p className="text-sm text-gray-600">{title}</p>
                    <p className="text-3xl font-bold mt-1">{value}</p>
                    {subtext && (
                        <p className="text-xs text-gray-500 mt-1">{subtext}</p>
                    )}
                </div>
                <div className={cn(
                    "h-12 w-12 rounded-xl flex items-center justify-center",
                    color
                )}>
                    <Icon className="h-6 w-6 text-white" />
                </div>
            </div>
        </div>
    );
}

function SuperAdminDashboard({ onNavigate }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    const fetchStats = useCallback(async (showLoading = true) => {
        try {
            if (showLoading) setLoading(true);
            setRefreshing(true);

            // Fetch stats from API
            const data = await superAdminService.getStats();

            setStats(data);
            setError(null);
        } catch (err) {
            logger.error('Error fetching stats:', err);
            setError('Failed to load statistics');
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center h-64">
                <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
                <p className="text-gray-600">{error}</p>
                <Button variant="link" onClick={() => fetchStats()}>
                    Try again
                </Button>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Platform Dashboard</h2>
                    <p className="text-gray-600">Overview of all tenants and usage</p>
                </div>
                <Button
                    variant="outline"
                    size="icon"
                    onClick={() => fetchStats(false)}
                    disabled={refreshing}
                >
                    <RefreshCw className={cn("h-4 w-4", refreshing && "animate-spin")} />
                </Button>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                    title="Total Tenants"
                    value={stats?.total_tenants || 0}
                    icon={Building2}
                    color="bg-blue-500"
                    subtext={`${stats?.active_tenants || 0} active`}
                />
                <StatCard
                    title="Total Users"
                    value={stats?.total_users || 0}
                    icon={Users}
                    color="bg-green-500"
                />
                <StatCard
                    title="Total Sessions"
                    value={stats?.total_sessions || 0}
                    icon={Activity}
                    color="bg-purple-500"
                />
                <StatCard
                    title="Total Messages"
                    value={stats?.total_messages || 0}
                    icon={MessageSquare}
                    color="bg-orange-500"
                />
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl border p-6 shadow-sm">
                <h3 className="font-semibold mb-4">Quick Actions</h3>
                <div className="flex flex-wrap gap-3">
                    <Button onClick={() => onNavigate?.('tenants')}>
                        <Building2 className="h-4 w-4 mr-2" />
                        Manage Tenants
                    </Button>
                    <Button variant="outline" onClick={() => onNavigate?.('usage')}>
                        <TrendingUp className="h-4 w-4 mr-2" />
                        View Usage
                    </Button>
                </div>
            </div>

            {/* Recent Activity Placeholder */}
            <div className="bg-white rounded-xl border p-6 shadow-sm">
                <h3 className="font-semibold mb-4">Recent Activity</h3>
                <div className="text-center py-8 text-gray-500">
                    <Activity className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p>Activity feed coming soon</p>
                </div>
            </div>
        </div>
    );
}

export { SuperAdminDashboard };
export default SuperAdminDashboard;
